// The Emarsys web-channel ribbons and overlay on the server-rendered pages, ported from
// `machtfit.startpage.emarsys`. While the member has not accepted Emarsys in Klaro, or its
// script fails to load, the campaigns come from the `emarsys-campaigns` operation instead.

import { callOperation } from "./utils/api"
import { WEB_CHANNEL_EVENT, WEB_CHANNEL_STATE } from "./utils/emarsys-web-channel"

// The key and the EDN value `machtfit.storage` writes, so a ribbon dismissed on either
// kind of page stays dismissed on the other.
const DISMISSED_KEY = "machtfit.app.startpage.dismissed-campaign-ids"
const FETCH_DELAY_MS = 3000
const dismissedOnThisPage = new Set()

function readDismissed() {
  try {
    const stored = localStorage.getItem(DISMISSED_KEY) ?? ""
    return new Set([
      ...Array.from(stored.matchAll(/"((?:[^"\\]|\\.)*)"|(-?\d+)/g), ([, text, number]) =>
        text === undefined ? Number(number) : JSON.parse(`"${text}"`),
      ),
      ...dismissedOnThisPage,
    ])
  } catch {
    return new Set(dismissedOnThisPage)
  }
}

function writeDismissed(ids) {
  const edn = Array.from(ids, (id) => (typeof id === "number" ? String(id) : JSON.stringify(id)))
  try {
    localStorage.setItem(DISMISSED_KEY, `#{${edn.join(" ")}}`)
  } catch {}
}

function emarsysAccepted() {
  try {
    return JSON.parse(decodeURIComponent(localStorage.getItem("klaro") ?? "{}"))?.emarsys === true
  } catch {
    return false
  }
}

function campaigns(answer) {
  return (answer?.ads ?? []).flatMap((campaign) => {
    try {
      const { document: content } = JSON.parse(campaign.c_content_v3)
      const [type] = Object.keys(content)
      return [{ id: campaign.id, type, html: content[type]?.content?.[0]?.html?.html }]
    } catch {
      return []
    }
  })
}

// Every ribbon, then the first overlay, as the SPA's startpage renders them. The SPA
// skips the overlay while a notification or a survey shows, and on the catalogue and
// the product detail; such a page empties `emarsys_overlay`. The checkout, the one shell
// page with a survey, empties the whole `emarsys` block, as the SPA showed none there.
// A shell notification is a modal: the overlay mounts behind it and becomes
// usable once the modal closes.
function visible(slot, answer) {
  const dismissed = readDismissed()
  const open = campaigns(answer).filter(({ id }) => !dismissed.has(id))
  const overlay =
    slot.querySelector("template[data-overlay]") && open.find(({ type }) => type === "overlay")
  return [...open.filter(({ type }) => type === "ribbon"), ...(overlay ? [overlay] : [])]
}

function render(slot, campaign, onDismiss) {
  const template = slot.querySelector(`template[data-${campaign.type}]`)
  const element = template.content.firstElementChild.cloneNode(true)
  element.id = campaign.id
  const shadowRoot = element.querySelector("[data-campaign]").attachShadow({ mode: "open" })
  shadowRoot.innerHTML = campaign.html ?? ""
  shadowRoot.getElementById("close")?.remove()
  element.querySelector("button").addEventListener("click", () => {
    dismissedOnThisPage.add(campaign.id)
    writeDismissed(readDismissed())
    onDismiss()
  })
  return element
}

function init() {
  const slot = document.getElementById("emarsys-ribbons")
  const root = document.getElementById("startpage")
  if (!slot || !root) {
    return
  }
  let shown = []
  let pending = null
  let requested = false

  function clear() {
    clearTimeout(pending)
    pending = null
    for (const ribbon of shown) {
      ribbon.remove()
    }
    shown = []
  }

  function show(campaign, answer) {
    const element = render(slot, campaign, () => {
      shown = shown.filter((other) => other !== element)
      const next = campaign.type === "overlay" ? nextOverlay(answer) : []
      element.replaceWith(...next)
    })
    shown.push(element)
    return element
  }

  function nextOverlay(answer) {
    return visible(slot, answer)
      .filter(({ type }) => type === "overlay")
      .map((campaign) => show(campaign, answer))
  }

  function load() {
    if (requested) {
      return
    }
    requested = true
    const timer = setTimeout(async () => {
      const envelope = await callOperation("emarsys-campaigns", [], {
        csrfToken: slot.dataset.csrf,
      })
      if (pending !== timer || !envelope?.success) {
        return
      }
      root.prepend(
        ...visible(slot, envelope.result).map((campaign) => show(campaign, envelope.result)),
      )
    }, FETCH_DELAY_MS)
    pending = timer
  }

  window.addEventListener(WEB_CHANNEL_EVENT, ({ detail }) => {
    if (detail.enabled) {
      load()
    } else {
      clear()
      requested = false
    }
  })
  if (window[WEB_CHANNEL_STATE] ?? !emarsysAccepted()) {
    load()
  }
}

init()
